// Live-grid orchestration for the jspdf-autotable based PDF engine. Same intent as the live
// exporter/pdfExporter.js's exportAsPdf(), but built generically from data (columns/rows/
// groupByFields/summary config) rendered via jspdf-autotable instead of DevExtreme's own
// exportDataGrid — see docs/pdf-export/session-summary.md's Topic 5 for the full story.
//
// Unlike the DevExtreme-based engine, this never touches the live grid's column visibility
// (no columnOption()/beginUpdate() dance) — finalColumnsForExport already carries the correct
// visible/groupIndex/format state from the shared cloneColumns/syncVisibility/processExportFields
// pipeline in ../exporter/index.js, so head/body are built directly from that.
const { jsPDF } = window.jspdf;
import autoTable from 'jspdf-autotable';
import { addHeaderFooterToDoc } from '../exporter/pdfHeaderFooter';
import { PDF_STYLES_V2 } from './pdfStylesV2';
import { buildGroupTree, annotateSummaries, computeGrandTotal } from './groupAndSummarize';
import { buildHead } from './buildHead';
import { buildBody, flattenLeafColumns } from './buildBody';
import { resolveFitToPageLayout } from './columnWidths';
import { buildHooksV2 } from './pdfHooksV2';
import { formatValue } from './valueFormatter';

const ORIENTATION_LANDSCAPE_COLUMN_THRESHOLD = 8; // matches the live engine's orientationPoint
const PX_TO_PT = 72 / 96; // CSS px (96/inch) → PDF points (72/inch), since doc uses unit: 'pt' — same conversion as the live engine's pdfExporter.js

// Mirrors gc-data-grid.vue's applyGroupLevelFontSize anchoring: gridOptions.options.groupLevelFontSizes
// is an array of CSS px sizes ordered outermost-first. Fewer active group levels than sizes
// configured anchors to the deepest/innermost size; more levels than configured anchors to the
// outermost, leaving extra levels at the default size. Returns { [level]: fontSizePt } or null.
function resolveGroupLevelFontSizesPt(sizes, activeLevelCount) {
    if (!sizes || !sizes.length || !activeLevelCount) return null;
    const result = {};
    for (let level = 0; level < activeLevelCount; level += 1) {
        const index = activeLevelCount <= sizes.length
            ? sizes.length - activeLevelCount + level
            : level;
        const px = sizes[index];
        if (px) result[level] = px * PX_TO_PT;
    }
    return result;
}

// Same pattern already proven in the live pdfExporter.js's measureRequiredNumericColumnWidths:
// fetch the full dataset (not just the currently-loaded page) matching the grid's current
// filter/sort, via the store's own load() rather than any DevExtreme export-internal API.
async function fetchFullDataset(grid) {
    const dataSource = grid.getDataSource && grid.getDataSource();
    const store = dataSource && dataSource.store && dataSource.store();
    if (!store || !store.load) return [];

    // dataSource.sort() excludes any grouped column — DevExtreme's own
    // getSortDataSourceParameters() filters out columns with a groupIndex, since a group
    // column's asc/desc order lives only in dataSource.group(). Passing `group` straight to
    // store.load() isn't an option either: DevExtreme's own query pipeline turns it into
    // nested {key, items} buckets, not the flat row array buildGroupTree/bucketByField
    // expect. So mirror what DevExtreme does internally: prepend group fields as sort
    // entries (skipping ones already explicitly sorted) ahead of the explicit sort, and
    // request only `sort` — this gets a flat array pre-ordered group-then-sort, matching
    // what the live grid shows.
    const explicitSort = (dataSource.sort ? dataSource.sort() : undefined) || [];
    const groupSort = (dataSource.group ? dataSource.group() : undefined) || [];
    const combinedSort = groupSort
        .filter((g) => !explicitSort.some((s) => s.selector === g.selector))
        .concat(explicitSort);

    const loadResult = await store.load({
        filter: dataSource.filter ? dataSource.filter() : undefined,
        sort: combinedSort.length ? combinedSort : undefined,
    });
    return (Array.isArray(loadResult) ? loadResult : (loadResult && loadResult.data)) || [];
}

// Identical to the live engine's groupByFields derivation — grid.getVisibleColumns() has been
// confirmed (via real debug logs against this exact grid) to include grouped columns here,
// so this is reused as-is rather than re-derived.
function getGroupByFields(grid) {
    const allColumns = grid.getVisibleColumns ? grid.getVisibleColumns() : [];
    return allColumns
        .filter((col) => typeof col.groupIndex === 'number')
        .sort((a, b) => a.groupIndex - b.groupIndex)
        .map((col) => col.dataField)
        .filter(Boolean);
}

function getSummaryConfig(grid) {
    const readOption = (path) => {
        try {
            return grid.option(path);
        } catch (err) {
            return undefined;
        }
    };
    return {
        groupItems: readOption('summary.groupItems') || [],
        totalItems: readOption('summary.totalItems') || [],
        calculateCustomSummary: readOption('summary.calculateCustomSummary'),
    };
}

export async function exportAsPdfV2({ fileName, grid, finalColumnsForExport, options }) {
    const visibleColumns = finalColumnsForExport.filter((col) => col.visible !== false);
    const orientation = flattenLeafColumns(visibleColumns).length > ORIENTATION_LANDSCAPE_COLUMN_THRESHOLD
        ? 'landscape'
        : 'portrait';

    const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4', compress: true });
    addHeaderFooterToDoc(doc, fileName, options);

    const rows = await fetchFullDataset(grid);
    const groupByFields = getGroupByFields(grid);
    const summary = getSummaryConfig(grid);

    const leafColumns = flattenLeafColumns(finalColumnsForExport);
    const ctx = { component: grid, calculateCustomSummary: summary.calculateCustomSummary };

    const tree = buildGroupTree(rows, groupByFields);
    annotateSummaries(tree, summary.groupItems, ctx);
    const grandTotal = summary.totalItems.length ? computeGrandTotal(tree, summary.totalItems, ctx) : {};

    const head = buildHead(finalColumnsForExport);
    const { body, rowMeta } = buildBody(tree, leafColumns, groupByFields, grandTotal, { formatValue });

    // minCellWidth (not cellWidth) — a numeric cellWidth makes autotable treat the column as
    // fixed and excludes it from its own proportional-shrink pass in resizeColumns(), so once a
    // report has enough columns their combined fixed widths alone can exceed the page and
    // autotable has no columns left it's allowed to shrink. minCellWidth keeps every column
    // resizable (still shrinkable to fit the page) while acting as a floor it won't cross, so
    // numeric values never truncate and header captions never get hard-broken letter-by-letter
    // (autotable's own resize fallback ignores word-boundaries once no floor is set at all).
    //
    // The floor for every column is measured at a font size chosen by resolveFitToPageLayout:
    // it shrinks the font (matching the live pdfExporter.js's fontScale policy) until the sum of
    // those floors actually fits the page, only going below the preferred readability size if
    // there's no other way to guarantee the fit.
    const availableWidth = doc.internal.pageSize.getWidth() - PDF_STYLES_V2.leftMargin - PDF_STYLES_V2.rightMargin;
    const { fontSize, numericWidths, headerMinWidths } = resolveFitToPageLayout({
        doc,
        leafColumns,
        rows,
        groupByFields,
        availableWidth,
        baseFontSize: PDF_STYLES_V2.defaultFontSize,
        headerFontSizeBoost: PDF_STYLES_V2.headerGroupFontSizeBoost,
        preferredMinFontSize: PDF_STYLES_V2.preferredMinFontSize,
    });

    const columnStyles = {};
    leafColumns.forEach((col, index) => {
        const floor = col.dataType === 'number' ? numericWidths[col.dataField] : headerMinWidths[col.dataField];
        if (floor) {
            columnStyles[index] = { minCellWidth: floor };
        }
    });

    const groupLevelFontSizesPt = resolveGroupLevelFontSizesPt(options.groupLevelFontSizes, groupByFields.length);
    const hooks = buildHooksV2({ rowMeta, fontSize, leafColumns, groupLevelFontSizesPt });

    autoTable(doc, {
        head,
        body,
        columnStyles,
        tableWidth: 'auto',
        styles: {
            fontSize,
            cellPadding: 2,
            lineColor: '#ffffff',
            lineWidth: 0.1,
            overflow: 'linebreak',
        },
        // Overrides autotable's default 'striped' theme, which otherwise fills every other
        // data row with a light gray band (see pdfStylesV2.js's alternateRowBgColor comment).
        alternateRowStyles: { fillColor: PDF_STYLES_V2.alternateRowBgColor },
        margin: {
            left: PDF_STYLES_V2.leftMargin,
            right: PDF_STYLES_V2.rightMargin,
            top: PDF_STYLES_V2.topMargin,
            bottom: PDF_STYLES_V2.bottomMargin,
        },
        showHead: 'everyPage',
        didParseCell: hooks.didParseCell,
        didDrawCell: hooks.didDrawCell,
        didDrawPage: hooks.didDrawPage,
    });

    doc.save(`${fileName}.pdf`);
}
